import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { useToast } from "@chakra-ui/react";
import { postFigureRedraftJob } from "../../../../api/ai/ai.service";
import { FoundationDataV2, Issb } from "../../../../api";
import { useAuth } from "../../../../context";
import { foundationV2Keys, issbKeys, useFoundationV2AnswerImpact } from "../../../../hooks/query/issb";
import { showErrorToast } from "../../../../utils";
import {
  decideOnImpact,
  IMPACT_GATE_ASK,
  uidsWorthChecking,
} from "../../../../utils/answerImpactGate";
import { IMPACT_OPTION } from "../../../../utils/answerImpactOptions";
import {
  optionRedrafts,
  optionReplacesFigures,
  redraftJobsFor,
  requirementsToComment,
} from "../../../../utils/answerImpactFollowUp";

export { IMPACT_OPTION };

/**
 * Asks what depends on a set of figures BEFORE changing them, and offers the choice if anything does.
 *
 * The pre-flight for "Manage changed data". Since ESG-4012 it guards the owner's APPROVAL of a workstream, not
 * each edit: edits only change the working set, and answers and AI Review first see a change when an approval
 * publishes it. `commit` is that approval. When no answer rests on the figures being published — much the
 * commonest case — it runs immediately and the user sees nothing new. When some do, the modal opens and
 * `commit` runs only if they confirm.
 *
 * <b>The options act on the ANSWERS, not on the figure change.</b> "Change nothing" still approves; Cancel
 * is what abandons the approval. So this gate wraps the write rather than following it.
 *
 * <b>It fails open.</b> A pre-flight that could not be fetched must not block a user from editing their own
 * data — that write was allowed before this feature existed. The change proceeds and a toast says the check
 * was skipped, so the choice is visibly lost rather than silently.
 *
 * <b>`commit` must return a promise that settles when the figure write has LANDED.</b> Both follow-ups read the
 * approved figures — the re-draft live at task-run time, the citation update when it runs — so starting either
 * before the approval commits would rewrite against the old numbers and report success. If the write fails,
 * neither should happen at all.
 */
/**
 * Runs a commit nobody is waiting on. Its own error handler has already reported a failure, so the rejection is
 * only swallowed here to keep it from surfacing a second time as an unhandled one.
 */
function runUnwatched(commit) {
  Promise.resolve()
    .then(commit)
    .catch(() => {});
}

export default function useAnswerImpactGate() {
  const toast = useToast();
  const { callApi } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(null);
  const [isFollowingUp, setFollowingUp] = useState(false);
  const { mutate: fetchImpact, isLoading: isChecking } = useFoundationV2AnswerImpact();

  const close = useCallback(() => setPending(null), []);

  const gate = useCallback(
    (datapointUids, commit) => {
      const uids = uidsWorthChecking(datapointUids);
      // Nothing to supersede — an added row, or a figure with no identity yet — so nothing to decide.
      if (uids.length === 0) {
        runUnwatched(commit);
        return;
      }
      fetchImpact(uids, {
        onSuccess: (impact) => {
          if (decideOnImpact(impact) === IMPACT_GATE_ASK) {
            setPending({ impact, commit, uids });
          } else {
            runUnwatched(commit);
          }
        },
        onError: () => {
          showErrorToast(
            toast,
            "Could not check which report answers use these figures. Saving the change anyway — re-check any answers that cite them."
          );
          // Fail open, loudly. Same rule as a successful-but-empty impact, which is what decideOnImpact(null)
          // says — asserted in test/utils/answerImpactGate.test.js so it cannot drift into blocking the write.
          if (decideOnImpact(null) === IMPACT_GATE_ASK) return;
          runUnwatched(commit);
        },
      });
    },
    [fetchImpact, toast]
  );

  /**
   * Option 2: enqueues one AI re-draft per affected round, once the figure change has landed. The drafts wait on
   * each answer's AI Draft tab — nothing is applied.
   *
   * Per round because a task carries one `questionnaire_id` and draws its reporting period from that round.
   * Failures are reported and do not throw: the figure change has already committed by this point, so a failed
   * enqueue must leave the user knowing the answers were not re-drafted, not thinking the save failed.
   */
  const enqueueRedrafts = useCallback(
    async (impact, comment) => {
      const { jobs, skipped } = redraftJobsFor(impact);
      /*
        No years means no approved figures to correct against, and an agent given none re-writes the answer from
        documents alone — the one thing this must not do. Happens when every affected citation predates the
        trace carrying a year, so it is reported rather than attempted.
      */
      if (jobs.length > 0 && (impact?.foundationYears?.length ?? 0) === 0) {
        showErrorToast(
          toast,
          "The figures were saved, but the affected answers do not record which reporting year they used, so they cannot be re-drafted automatically. Update them by hand."
        );
        return;
      }
      let enqueued = 0;
      for (const job of jobs) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await postFigureRedraftJob({
            questionnaireId: job.roundId,
            questionIds: job.questionIds,
            foundationYears: job.foundationYears,
            // Always a draft: options 3 and 4 no longer re-draft (see replaceFigures).
            draft: true,
            instructions: comment || undefined,
          });
          enqueued += job.questionIds.length;
        } catch (err) {
          showErrorToast(
            toast,
            `Could not start the re-draft for one round: ${
              err.response?.data?.detail ?? err.message
            }. The figures were saved — the answers in that round still cite the old ones.`
          );
        }
      }
      if (enqueued > 0) {
        queryClient.invalidateQueries("taskService.list");
        toast({
          title: `Drafting updates for ${enqueued} answer${enqueued === 1 ? "" : "s"}`,
          description: "The drafts appear on each answer's AI Draft tab. Nothing is applied until you apply it.",
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      }
      if (skipped.length > 0) {
        // Said out loud rather than folded into the count: these belong to no round, so there was nothing to
        // enqueue them against and nothing happened to them.
        showErrorToast(
          toast,
          `${skipped.length} answer${skipped.length === 1 ? "" : "s"} could not be re-drafted because ${
            skipped.length === 1 ? "it belongs" : "they belong"
          } to no reporting round. Update ${skipped.length === 1 ? "it" : "them"} by hand.`
        );
      }
    },
    [queryClient, toast]
  );

  /**
   * Records the user's comment on every affected requirement — for ALL FOUR options.
   *
   * The comment explains why the figures moved. That is worth keeping whether the answers were re-drafted,
   * reopened, or deliberately left alone: "change nothing" is a decision, and the note explaining it is the only
   * record that decision leaves.
   *
   * Every requirement gets it, not only the ones an option acts on — including one belonging to no round, which
   * the stage move and the re-draft both have to skip. A comment needs no round.
   *
   * A failure here does NOT stop the rest. By this point the figure change has committed, and losing the note is
   * worse reported than it is as a reason to abandon the follow-up the user asked for.
   */
  const recordComment = useCallback(
    async (impact, comment) => {
      const text = (comment ?? "").trim();
      const questionIds = requirementsToComment(impact);
      // The server 400s on a blank comment, on the grounds that asking to record nothing is a caller bug. So
      // "the user typed nothing" is decided here, where it is not one.
      if (!text || questionIds.length === 0) {
        return;
      }
      try {
        const result = await callApi(Issb.commentOnAnswers, { questionIds, comment: text });
        const missed = result?.notFound?.length ?? 0;
        if (missed > 0) {
          showErrorToast(
            toast,
            `Your comment was added to ${result?.commented?.length ?? 0} answer${
              (result?.commented?.length ?? 0) === 1 ? "" : "s"
            }, but ${missed} could no longer be found.`
          );
        }
      } catch (err) {
        showErrorToast(
          toast,
          `Your comment could not be saved: ${
            err.response?.data?.parameters?.message ?? err.response?.data?.message ?? err.message
          }. Everything else went ahead.`
        );
      }
    },
    [callApi, toast]
  );

  /**
   * Options 3 and 4: rewrites each affected answer's inline citations onto the newly approved figures — a
   * find-and-replace on the server, not an AI re-draft, so the wording a person accepted stays exactly as it was.
   *
   * Option 3's stage move happens in the same server transaction, over the answers that were actually updated,
   * so a reopen can no longer be lost between two calls.
   *
   * Reports what ACTUALLY happened, from the response. An answer that uses a figure with no inline citation has
   * nothing to rewrite — that is said out loud, because the modal counted it as affected and it still is.
   */
  const replaceFigures = useCallback(
    async (impact, uids, reopen) => {
      const questionIds = requirementsToComment(impact);
      if (questionIds.length === 0) return;
      try {
        const result = await callApi(FoundationDataV2.updateAnswerFigures, {
          questionIds,
          datapointUids: uids,
          returnToProgress: reopen,
        });
        const updated = result?.updated?.length ?? 0;
        const moved = result?.reopened?.moved?.length ?? 0;
        const noCitation = (result?.skipped ?? []).filter((skip) => skip.reason === "no-inline-citation").length;
        const plural = (n) => (n === 1 ? "" : "s");
        toast({
          title:
            updated > 0
              ? `Updated the figures in ${updated} disclosure${plural(updated)}`
              : "No disclosure needed its figures updated",
          description:
            [
              reopen && updated > 0 ? `${moved} returned to In progress for review.` : null,
              "Only the cited figures changed — check any wording that restates them.",
            ]
              .filter(Boolean)
              .join(" ") || undefined,
          status: updated > 0 ? "success" : "info",
          duration: 7000,
          isClosable: true,
        });
        if (noCitation > 0) {
          showErrorToast(
            toast,
            `${noCitation} disclosure${plural(noCitation)} use${noCitation === 1 ? "s" : ""} these figures without an inline citation, so there was nothing to replace. Update ${noCitation === 1 ? "it" : "them"} by hand.`
          );
        }
        if (updated > 0) {
          // The usage badges and the answers both read the citations that just moved.
          queryClient.invalidateQueries(foundationV2Keys.curatedUsage());
          queryClient.invalidateQueries(["foundationV2QuestionUsage"]);
          queryClient.invalidateQueries(issbKeys.answer());
          queryClient.invalidateQueries(issbKeys.questionList());
        }
      } catch (err) {
        showErrorToast(
          toast,
          `The figures were approved, but the disclosures could not be updated: ${
            err.response?.data?.parameters?.message ?? err.response?.data?.message ?? err.message
          }. They still cite the old figures.`
        );
      }
    },
    [callApi, queryClient, toast]
  );

  const confirm = useCallback(
    async (option, comment) => {
      const { commit, impact, uids } = pending ?? {};
      const chosen = option ?? IMPACT_OPTION.NOTHING;
      close();
      if (!commit) return;
      setFollowingUp(true);
      try {
        // The figure change always happens — including for "change nothing", which is about the ANSWERS.
        // Awaited because the re-draft reads the approved figures live; enqueued first, it would read the old
        // ones. A throw here skips the follow-up entirely, which is right: nothing changed to re-draft for.
        await commit({ option: chosen, comment: comment ?? "" });
        /*
          For every option, including "change nothing", and after the write rather than before: a comment
          explaining a figure change that was then abandoned is a note about something that did not happen.
        */
        await recordComment(impact, comment);
        if (optionReplacesFigures(chosen)) {
          await replaceFigures(impact, uids, chosen === IMPACT_OPTION.APPLY_AND_REOPEN);
        } else if (optionRedrafts(chosen)) {
          await enqueueRedrafts(impact, comment);
        }
      } catch (err) {
        // The write's own handler has already surfaced this — a second toast would just repeat it. What matters
        // here is that nothing was rewritten against figures that did not change.
        console.warn("Figure change failed; no answers updated", err);
      } finally {
        setFollowingUp(false);
      }
    },
    [pending, close, enqueueRedrafts, replaceFigures, recordComment]
  );

  return {
    gate,
    isChecking,
    modal: {
      isOpen: !!pending,
      impact: pending?.impact,
      onConfirm: confirm,
      onCancel: close,
      isBusy: isFollowingUp,
      enabledOptions: [
        IMPACT_OPTION.NOTHING,
        IMPACT_OPTION.DRAFT,
        IMPACT_OPTION.APPLY,
        IMPACT_OPTION.APPLY_AND_REOPEN,
      ],
    },
  };
}
